import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { saveAs } from 'file-saver';

const ExportContainer = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ExportSection = styled.div`
  background-color: #f0f0f0;
  padding: 15px;
  border-radius: 8px;
`;

const SectionTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 15px;
  font-size: 16px;
  color: #333;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const ExportButton = styled.button`
  padding: 8px 16px;
  background-color: #333;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  
  &:hover {
    background-color: #555;
  }
`;

const ScaleOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 15px;
`;

const ScaleOption = styled.button`
  padding: 5px 10px;
  background-color: ${props => props.selected ? '#555' : '#ddd'};
  color: ${props => props.selected ? 'white' : '#333'};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background-color: ${props => props.selected ? '#555' : '#ccc'};
  }
`;

const CustomSizeContainer = styled.div`
  display: ${props => props.visible ? 'flex' : 'none'};
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
  padding: 10px;
  background-color: #e0e0e0;
  border-radius: 4px;
`;

const DimensionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DimensionInput = styled.input`
  width: 80px;
  padding: 5px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const DimensionLabel = styled.span`
  min-width: 60px;
`;

const MaintainAspectRatio = styled.label`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 5px;
  cursor: pointer;
`;

const ExportPanel = ({ svgCode }) => {
  const [scale, setScale] = useState(1);
  const [customSize, setCustomSize] = useState(false);
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  const originalDimensions = useRef({ width: 0, height: 0 });
  const aspectRatio = useRef(1);
  
  // Extract original dimensions from SVG when it changes
  useEffect(() => {
    try {
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgCode, 'image/svg+xml');
      const svgElement = svgDoc.querySelector('svg');
      
      if (svgElement) {
        const svgWidth = parseInt(svgElement.getAttribute('width') || '300');
        const svgHeight = parseInt(svgElement.getAttribute('height') || '300');
        
        originalDimensions.current = { width: svgWidth, height: svgHeight };
        aspectRatio.current = svgWidth / svgHeight;
        
        // Initialize custom dimensions
        setWidth(svgWidth.toString());
        setHeight(svgHeight.toString());
      }
    } catch (error) {
      console.error('Error parsing SVG dimensions:', error);
    }
  }, [svgCode]);
  
  const handleScaleSelect = (newScale) => {
    setScale(newScale);
    setCustomSize(false);
  };
  
  const handleCustomSizeSelect = () => {
    setCustomSize(true);
    setScale(null);
  };
  
  const handleWidthChange = (e) => {
    const newWidth = e.target.value;
    setWidth(newWidth);
    
    if (maintainAspectRatio && newWidth !== '') {
      const numericWidth = parseInt(newWidth);
      if (!isNaN(numericWidth)) {
        const newHeight = Math.round(numericWidth / aspectRatio.current);
        setHeight(newHeight.toString());
      }
    }
  };
  
  const handleHeightChange = (e) => {
    const newHeight = e.target.value;
    setHeight(newHeight);
    
    if (maintainAspectRatio && newHeight !== '') {
      const numericHeight = parseInt(newHeight);
      if (!isNaN(numericHeight)) {
        const newWidth = Math.round(numericHeight * aspectRatio.current);
        setWidth(newWidth.toString());
      }
    }
  };
  
  const handleExportSVG = () => {
    const blob = new Blob([svgCode], { type: 'image/svg+xml;charset=utf-8' });
    saveAs(blob, 'exported-image.svg');
  };
  
  const handleExportPNG = () => {
    exportRaster('png');
  };
  
  const handleExportJPG = () => {
    exportRaster('jpeg');
  };
  
  const exportRaster = (format) => {
    try {
      // Create a canvas element
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Create an image from the SVG
      const img = new Image();
      const svgBlob = new Blob([svgCode], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      
      img.onload = () => {
        // Get SVG dimensions
        // const parser = new DOMParser();
        // const svgDoc = parser.parseFromString(svgCode, 'image/svg+xml');
        
        let outputWidth, outputHeight;
        
        if (customSize) {
          // Use custom dimensions
          outputWidth = parseInt(width) || originalDimensions.current.width;
          outputHeight = parseInt(height) || originalDimensions.current.height;
        } else {
          // Use scale
          outputWidth = originalDimensions.current.width * scale;
          outputHeight = originalDimensions.current.height * scale;
        }
        
        // Set canvas size
        canvas.width = outputWidth;
        canvas.height = outputHeight;
        
        // Fill with white background for JPG
        if (format === 'jpeg') {
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, outputWidth, outputHeight);
        }
        
        // Draw the image
        ctx.drawImage(img, 0, 0, outputWidth, outputHeight);
        
        // Convert to blob and download
        canvas.toBlob((blob) => {
          saveAs(blob, `exported-image.${format === 'jpeg' ? 'jpg' : format}`);
          URL.revokeObjectURL(url);
        }, `image/${format}`);
      };
      
      img.src = url;
    } catch (error) {
      console.error('Error exporting image:', error);
      alert('Error exporting image. Please try again.');
    }
  };
  
  return (
    <ExportContainer>
      <ExportSection>
        <SectionTitle>Export Options</SectionTitle>
        
        <ScaleOptions>
          <ScaleOption 
            selected={scale === 0.1 && !customSize} 
            onClick={() => handleScaleSelect(0.1)}
          >
            0.1x
          </ScaleOption>
          <ScaleOption 
            selected={scale === 0.5 && !customSize} 
            onClick={() => handleScaleSelect(0.5)}
          >
            0.5x
          </ScaleOption>
          <ScaleOption 
            selected={scale === 1 && !customSize} 
            onClick={() => handleScaleSelect(1)}
          >
            1x
          </ScaleOption>
          <ScaleOption 
            selected={scale === 2 && !customSize} 
            onClick={() => handleScaleSelect(2)}
          >
            2x
          </ScaleOption>
          <ScaleOption 
            selected={scale === 4 && !customSize} 
            onClick={() => handleScaleSelect(4)}
          >
            4x
          </ScaleOption>
          <ScaleOption 
            selected={customSize} 
            onClick={handleCustomSizeSelect}
          >
            Custom
          </ScaleOption>
        </ScaleOptions>
        
        <CustomSizeContainer visible={customSize}>
          <DimensionsRow>
            <DimensionLabel>Width:</DimensionLabel>
            <DimensionInput 
              type="number" 
              value={width} 
              onChange={handleWidthChange}
              min="1"
            />
            <span>px</span>
          </DimensionsRow>
          
          <DimensionsRow>
            <DimensionLabel>Height:</DimensionLabel>
            <DimensionInput 
              type="number" 
              value={height} 
              onChange={handleHeightChange}
              min="1"
            />
            <span>px</span>
          </DimensionsRow>
          
          <MaintainAspectRatio>
            <input 
              type="checkbox" 
              checked={maintainAspectRatio} 
              onChange={() => setMaintainAspectRatio(!maintainAspectRatio)}
              id="aspect-ratio"
            />
            <span>Maintain aspect ratio</span>
          </MaintainAspectRatio>
        </CustomSizeContainer>
        
        <ButtonGroup>
          <ExportButton onClick={handleExportSVG}>
            Export SVG
          </ExportButton>
          <ExportButton onClick={handleExportPNG}>
            Export PNG
          </ExportButton>
          <ExportButton onClick={handleExportJPG}>
            Export JPG
          </ExportButton>
        </ButtonGroup>
      </ExportSection>
    </ExportContainer>
  );
};

export default ExportPanel; 